import React, { useMemo } from 'react';
import CardCommon from '@op/components/CardCommon';
import FilterBoxCommon from '@op/components/FilterBoxCommon';
import { useDispatch, useIntl } from 'umi';

import TableCommonHook from '@op/components/TableCommonHook';
import useChannelShareRankings from '@op/components/TableCommonHook/hooks/useChannelShareRankings';
import { useTableData } from '../hooks';
import { Table } from 'tea-component';
import useTableSelect from '@op/hooks/useTableSelect';
import Trend from '../Trend';
import { useDownloadParams } from '@op/hooks/useDownloadParams';
import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import './index.scss';
const tableKey = 'game_id';
const { radioable } = Table.addons;

export default function GameRankingList({
  filter,
  selectedGameInfo,
  changeGameInfo,
  tab,
}: any) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const { download, downloading } = useDownloadParams();
  // 表格全选框状态， 选中的相关数据
  const {
    saveDefaultData,
    selectKeys,
    allSelectInfo,
    selectedKeysMap,
    commonIndexColumns,
  } = useTableSelect({
    tableKey,
  });

  const {
    tableRecode,
    currentGame,
    total,
    sort,
    setSort,
    pageInfo,
    searchValue,
    changePageInfo,
    setSearchValue,
    loading,
    gameInfoMap,
    gameInfoList,
  } = useTableData({
    filter,
    saveDefaultData,
  });

  // 分页数据处理：当前游戏始终在第一个位置，然后进行分页
  const displayRecords = useMemo(() => {
    if (!tableRecode || tableRecode.length === 0) return [];

    const currentPage = pageInfo.page;
    const pageSize = pageInfo.page_size;

    // 所有页面：currentGame + 其他数据的分页数据
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const pageItems = tableRecode.slice(startIndex, endIndex);

    return currentGame ? [currentGame, ...pageItems] : pageItems;
  }, [tableRecode, currentGame, pageInfo]);

  function toggleExpand(visible = false) {
    dispatch({
      type: 'opinionMenu/toggleMenuExpand',
      payload: {
        menuExpand: visible,
      },
    });
  }

  const columns = useChannelShareRankings({
    selectGameList: gameInfoList,
    toggleExpand,
    sort,
    setSort,
    filter: {
      start_time: filter.start_time,
      end_time: filter.end_time,
      date_type: filter.date_type,
    },
  });

  const trendFilter = useMemo(() => {
    if (loading) return null;

    const selectGames = selectKeys
      .map((item) => {
        const gameInfo = gameInfoMap?.[item] || {};
        if (gameInfo?.edition_unified_id) {
          return {
            edition_unified_id: gameInfo.edition_unified_id,
            entity_type: gameInfo.entity_type,
            id_type: gameInfo.id_type,
          };
        }
      })
      .filter(Boolean);

    if (selectGames.length === 0) {
      return null;
    }

    return {
      ...filter,
      timeline_games: selectGames,
    };
  }, [selectKeys, loading]);

  return (
    <>
      <CardCommon
        className="dbo-channel-share-ranking-list"
        title={
          <div className="card-common-title">
            <label className="title-info">
              {formatMessage({ id: 'Game Ranking List' })}{' '}
              {tab === 'mobile' || searchValue?.trim() ? '' : formatMessage({ id: '(Top 100)' })}
            </label>
            <div className="dbo-title-info-tips">
              {' '}
              {formatMessage({
                id: 'Click the table to view the details of comments.',
              })}
            </div>
          </div>
        }
        operations={[
          {
            element: (
              <FilterBoxCommon
                className="dbo-channel-share-ranking-filter"
                left={[]}
                downloadLoading={downloading}
                right={[
                  {
                    key: 'search',
                    className: 'search-input-box',
                    defaultParams: {
                      placeholder: formatMessage({
                        id: 'Search Any Game in the table',
                      }),
                      onSearch: (value: string) => {
                        setSearchValue(value);
                        buriedBtnPoint('SearchGame');
                      },
                      onClear: () => setSearchValue(''),
                    },
                  },
                  {
                    key: 'download',
                    className: 'dbo-channel-share-ranking-download',
                    event: { 
                      onClick: () => {
                        if (filter?.channels.length === 0) {
                          download({
                            key: 'CompareChannelShareRankingTableTotal',
                            fileName: 'Channel Share Ranking',
                            filter,
                            auxiliaryParams: {
                              data: tableRecode,
                            }
                          });
                        } else {
                          download({
                            key: 'CompareChannelShareRankingTable',
                            fileName: 'Channel Share Ranking',
                            filter,
                            data: tableRecode,
                          });
                        }
                        buriedBtnPoint('Download');
                      },
                    },
                  },
                ]}
                onChange={() => { }}
              ></FilterBoxCommon>
            ),
          },
        ]}
        loading={loading}
        loadingHeight={842}
        empty={total === 0}
        emptyHeight={400}
      >
        <TableCommonHook
          recordKey={tableKey}
          records={displayRecords}
          total={total}
          rowClassName={(record: any) => {
            return record?.game_id === filter?.edition_unified_id ? 'dbo-current-game-row' : '';
          }}
          columns={[
            commonIndexColumns({
              headerInfo: (
                <span className="dbo-influencer-table-header">
                  {formatMessage({ id: 'Rank' })}
                </span>
              ),
              width: 110,
              dealwithItemElement: ({ record }) => record.rank,
              pageInfo,
              hasAllSelect: false,
            }),
            ...columns,
          ]}
          pageInfo={pageInfo}
          setPageInfo={changePageInfo}
          hasPageable={true}
          bordered="all"
          addons={[
            radioable({
              value: selectedGameInfo?.edition_unified_id,
              onChange: (key) => {
                const value = gameInfoMap[key];
                if (value) {
                  toggleExpand();
                }
                buriedBtnPoint('OpenGameDetail', value);
                changeGameInfo(value);
              },
              rowSelect: true,
            }),
            
          ]}
          maxHeight={735}
        ></TableCommonHook>
      </CardCommon>

      <Trend
        selectedKeysMap={selectedKeysMap}
        gameInfoMap={gameInfoMap}
        filter={trendFilter}
        loading={loading}
      ></Trend>
    </>
  );
}
